import { Box, Checkbox, Divider, Group, NumberInput, Stack, Switch, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useEffect, useRef, useState } from 'react';

import { lovLabel, orderFacetValues } from '../engine';
import { useCatalogLabels } from '../labels';
import {
    loc,
    type ActiveFilters,
    type CatalogLabels,
    type ComputedFacet,
    type DateRangeValue,
    type FilterValue,
    type RangeValue,
} from '../types';
import { facetFilterChips } from './ActiveFilterChips';
import { dateToIso, isoToDate } from './dateIso';
import { CountedLabel, FACET_ROW_STYLES, FacetGroup, FacetValueList } from './FacetGroup';
import { OVERLAY_DROPDOWN_Z_INDEX } from './headerLayout';

interface FacetsPanelProps {
    facets: ComputedFacet[];
    filters: ActiveFilters;
    locale: string;
    /** LOV code -> display label lookup (labels.<lang>.json) - see FacetDef.attrId. */
    labels: CatalogLabels;
    onChange: (filters: ActiveFilters) => void;
}

/** The von/bis fields: compact, thin grey frame (dark blue on focus, App.css). */
const RANGE_INPUT_STYLE = {
    height: 36,
    minHeight: 36,
    fontSize: 14,
    paddingInline: 10,
    borderRadius: 4,
    border: '1px solid var(--pih-border)'
} as const;

interface RangeRowProps {
    facet: ComputedFacet;
    value?: RangeValue;
    onChange: (value: RangeValue | undefined) => void;
}

/** A NumberInput hands back `number | string` ('' while empty, or a half-typed "4,"). */
const toBound = (raw: number | string): number | undefined => {
    if (typeof raw === 'number') {
        return Number.isFinite(raw) ? raw : undefined;
    }
    const parsed = Number(raw.replace(',', '.'));
    return raw.trim() === '' || Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * A `range` facet as two typed bounds (see chat: "Können wir für Länge, Breite, Höhe ein 'von' …
 * 'bis' implementieren? Ohne Slider?", and later for Gewicht: "nicht den Schieberegler sondern
 * von ... bis mit zwei Feldern"). This is now the ONLY control for range facets - the former
 * RangeSlider variant (`control: "range"`) is gone, so a served catalog-config.json that still
 * says "range" (the blob-seeded copy lagged behind the repo, which is how the slider survived the
 * config change) can't bring it back. The filter itself: engine.applyFilters evaluates a `range`
 * filter as `(min == null || n >= min) && (max == null || n <= max)`, i.e. inclusive on both ends.
 *
 * That inclusiveness is what answers the customer's actual question ("Ich suche ein Produkt mit der
 * Länge 4,2 und Höhe 3,5"): the same value in both fields is an exact match, one field alone is an
 * open "ab"/"bis", and two such facets combine with AND across facets - so Länge 4,2 + Höhe 3,5
 * lands on exactly the articles that have both.
 *
 * Committed on blur/Enter rather than on every keystroke: a half-typed "4," would otherwise narrow
 * the list to nothing for a moment and, worse, be committed as 4.
 */
const MinMaxRow = ({ facet, value, onChange }: RangeRowProps) => {
    const { L, locale } = useCatalogLabels();
    const bounds = facet.bounds;
    const [draft, setDraft] = useState<{ min: number | string; max: number | string }>({
        min: value?.min ?? '',
        max: value?.max ?? ''
    });

    useEffect(() => {
        setDraft({ min: value?.min ?? '', max: value?.max ?? '' });
    }, [value?.min, value?.max]);

    // A value typed but not yet committed (no blur, no Enter) when the row goes away - the drawer
    // closed with Escape, say - is committed on the way out instead of silently dropped (review
    // point 9). Set further down, once `commit` exists; only while the fields differ from `value`.
    const pendingCommitRef = useRef<(() => void) | null>(null);
    useEffect(() => () => pendingCommitRef.current?.(), []);
    const [focused, setFocused] = useState(false);
    const formatBound = (n: number) =>
        new Intl.NumberFormat(locale === 'en' ? 'en' : 'de', { maximumFractionDigits: 3 }).format(n);

    if (!bounds) {
        // Same empty-data case DateRangeRow spells out: no article currently carries a value for
        // this attribute, so two input fields would just be a broken-looking widget.
        return (
            <Text c="dimmed" size="sm">
                {L.noData}
            </Text>
        );
    }

    const commit = (next: { min: number | string; max: number | string }) => {
        const min = toBound(next.min);
        const max = toBound(next.max);
        if (min === undefined && max === undefined) {
            onChange(undefined);
            return;
        }
        // "von 5 bis 2" can only ever be empty - swapping is what the user meant, and beats
        // silently showing them nothing.
        const swap = min !== undefined && max !== undefined && min > max;
        onChange(swap ? { min: max, max: min } : { min, max });
    };
    const isDirty = toBound(draft.min) !== value?.min || toBound(draft.max) !== value?.max;
    pendingCommitRef.current = isDirty ? () => commit(draft) : null;

    const inputProps = {
        allowNegative: false,
        // A German user types "4,2"; without this the comma is rejected and the value read as 42.
        decimalSeparator: locale === 'en' ? '.' : ',',
        hideControls: true,
        size: 'xs' as const,
        // Inline, so @uag/doka-design's tall input frame can't win: one compact, quiet field. The
        // unit no longer sits inside the field - doka-design pushed it into the top corner
        // (reported: "Die Filter sehen noch nicht richtig schön aus"), and the heading carries it.
        styles: { input: RANGE_INPUT_STYLE },
        onFocus: () => setFocused(true)
    };

    return (
        <Stack gap={6}>
            <Group align="center" gap={8} wrap="nowrap">
                <NumberInput
                    {...inputProps}
                    aria-label={`${loc(facet.label, locale, facet.key)} ${L.facetFrom}`}
                    placeholder={`${L.facetFrom} ${formatBound(bounds.min)}`}
                    style={{ flex: 1 }}
                    value={draft.min}
                    onBlur={() => {
                        setFocused(false);
                        commit(draft);
                    }}
                    onChange={(next) => setDraft((prev) => ({ ...prev, min: next }))}
                    onKeyDown={(event) => event.key === 'Enter' && commit(draft)}
                />
                <Text c="dimmed" size="sm" aria-hidden>
                    –
                </Text>
                <NumberInput
                    {...inputProps}
                    aria-label={`${loc(facet.label, locale, facet.key)} ${L.facetTo}`}
                    placeholder={`${L.facetTo} ${formatBound(bounds.max)}`}
                    style={{ flex: 1 }}
                    value={draft.max}
                    onBlur={() => {
                        setFocused(false);
                        commit(draft);
                    }}
                    onChange={(next) => setDraft((prev) => ({ ...prev, max: next }))}
                    onKeyDown={(event) => event.key === 'Enter' && commit(draft)}
                />
            </Group>
            {/* Only while a field is being edited: printed under every range facet at once it
                was the same sentence four times over. */}
            {focused && (
                <Text c="dimmed" size="xs">
                    {L.facetExactHint}
                </Text>
            )}
        </Stack>
    );
};

interface DateRangeRowProps {
    facet: ComputedFacet;
    value?: DateRangeValue;
    onChange: (value: DateRangeValue | undefined) => void;
    noDataLabel: string;
}

const DateRangeRow = ({ facet, value, onChange, noDataLabel }: DateRangeRowProps) => {
    const bounds = facet.dateBounds;
    if (!bounds) {
        // No article currently carries a value for this facet's attribute (e.g. the index hasn't
        // been (re-)converted since this attribute was added, or the source data is empty for it) -
        // say so explicitly rather than silently rendering nothing under the label, which looks
        // like a broken widget instead of an empty-data state.
        return (
            <Text c="dimmed" size="sm">
                {noDataLabel}
            </Text>
        );
    }
    return (
        // popoverProps.zIndex: the calendar is a portaled Mantine Popover at the default z-index
        // 300, while the filter drawer this row lives in sits at 1000 (App.css, .mantine-Drawer-
        // inner) - so the calendar opened BEHIND the drawer and the field looked dead ("Im Filter
        // kann ich 'Planbar ab' nicht filtern"). Same fix class as the Selects in the detail
        // overlay, see OVERLAY_DROPDOWN_Z_INDEX.
        <DatePickerInput
            maxDate={isoToDate(bounds.max) ?? undefined}
            minDate={isoToDate(bounds.min) ?? undefined}
            popoverProps={{ zIndex: OVERLAY_DROPDOWN_Z_INDEX }}
            size="xs"
            type="range"
            value={[isoToDate(value?.from), isoToDate(value?.to)]}
            valueFormat="DD.MM.YYYY"
            clearable
            onChange={([from, to]) => {
                const next: DateRangeValue = { from: dateToIso(from), to: dateToIso(to) };
                onChange(next.from || next.to ? next : undefined);
            }}
        />
    );
};

export const FacetsPanel = ({ facets, filters, locale, labels, onChange }: FacetsPanelProps) => {
    const { L } = useCatalogLabels();

    // The same wording as the filter chips above the list ("0,1 – 2 kg", the value labels).
    const summaryOf = (key: string) =>
        filters[key] === undefined
            ? undefined
            : facetFilterChips({ [key]: filters[key] }, facets, labels, locale, L, () => undefined)
                  .map((chip) => chip.value)
                  .join(', ');

    const setFilter = (key: string, value: FilterValue | undefined) => {
        const next = { ...filters };
        if (value === undefined) {
            delete next[key];
        } else {
            next[key] = value;
        }
        onChange(next);
    };

    // "Zurücksetzen" lives in the drawer title now (FilterDrawerTitle), not in here.
    return (
        <Stack gap="md">
            {facets.map((facet, index) => (
                <Box key={facet.key}>
                    {index > 0 && <Divider color="var(--pih-border)" mb="md" />}
                    {/* Collapsible group with bold heading, and for value lists the right-aligned
                        count column plus the "N weitere anzeigen" cap - all shared with
                        DocumentFacetsPanel, see FacetGroup.tsx. */}
                    <FacetGroup
                        groupKey={facet.key}
                        summary={summaryOf(facet.key)}
                        title={`${loc(facet.label, locale, facet.key)}${facet.unit ? ` (${facet.unit})` : ''}`}
                    >
                        {facet.type === 'value-list' && facet.values && (
                            <Checkbox.Group
                                value={(filters[facet.key] as string[]) ?? []}
                                onChange={(values) => setFilter(facet.key, values.length ? values : undefined)}
                            >
                                <FacetValueList
                                    entries={orderFacetValues(facet.values, facet, labels, locale)}
                                    labelOf={(entry) => lovLabel(labels, facet, entry.value)}
                                    renderEntry={(entry) => (
                                        <Checkbox
                                            key={entry.value}
                                            // A selected value stays operable at count 0 (same rule
                                            // as DocumentFacetsPanel): with the assortment filters
                                            // now pre-applied from the landing page, a later
                                            // Gewicht/Planbar-ab choice can zero out e.g. "Alle
                                            // Länder" - and a checked, DISABLED box would leave no
                                            // way out but "Zurücksetzen".
                                            disabled={
                                                entry.count === 0 &&
                                                !((filters[facet.key] as string[] | undefined) ?? []).includes(entry.value)
                                            }
                                            label={
                                                <CountedLabel
                                                    count={entry.count}
                                                    text={lovLabel(labels, facet, entry.value)}
                                                />
                                            }
                                            styles={FACET_ROW_STYLES}
                                            value={entry.value}
                                        />
                                    )}
                                    selected={(filters[facet.key] as string[]) ?? []}
                                />
                            </Checkbox.Group>
                        )}

                        {facet.type === 'range' && (
                            <MinMaxRow
                                facet={facet}
                                value={filters[facet.key] as RangeValue | undefined}
                                onChange={(range) => setFilter(facet.key, range)}
                            />
                        )}

                        {facet.type === 'date-range' && (
                            <DateRangeRow
                                facet={facet}
                                noDataLabel={L.noData}
                                value={filters[facet.key] as DateRangeValue | undefined}
                                onChange={(range) => setFilter(facet.key, range)}
                            />
                        )}

                        {/* Same row layout as a value-list entry (label left, count right) instead
                            of the former "Ja (N)" text, which @uag/doka-design's Switch styling cut
                            down to "J." in the review screenshot. */}
                        {facet.type === 'boolean' && facet.counts && (
                            <Switch
                                checked={filters[facet.key] === true}
                                label={<CountedLabel count={facet.counts.true} text={L.yes} />}
                                styles={FACET_ROW_STYLES}
                                onChange={(event) =>
                                    setFilter(facet.key, event.currentTarget.checked ? true : undefined)
                                }
                            />
                        )}
                    </FacetGroup>
                </Box>
            ))}
        </Stack>
    );
};
